import forgotpassword from "assets/forgotpassword.png";
import logo from "assets/logo_verify.svg";
import { Box, Typography, Grid, Link as MuiLink, Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { object, string, TypeOf } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { LoadingButton as _LoadingButton } from "@mui/lab";
import lock from "assets/img-pass.svg";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  useResetPasswordMutation,
  useVerifyPinMutation,
} from "../../redux/api/authApi";
import { Field } from "components/Shared";
import FormInput from "components/FormInput";
import { showErrorToast, showSuccessToast } from "components/Toast/Toast";
import { useIntl } from "react-intl";


const LinkItem = styled(Link)`
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const pinSchema = object({
  pin: string()
    .min(1, "El pin es obligotrio")
    .min(4, "El pin debe tener al menos 4 caracteres"),
});

export type PinInput = TypeOf<typeof pinSchema>;

export const PinPage = () => {
  const { formatMessage } = useIntl();
  const { resetToken } = useParams<{ resetToken: string }>();

  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const [pin, setPin] = useState<string[]>(["", "", "", ""]);

  const methods = useForm<PinInput>({});

  // 👇 API Login Mutation
  const [sendPin, { isLoading, isError, error, isSuccess }] =
    useVerifyPinMutation();

  const navigate = useNavigate();

  const {
    reset,
    handleSubmit,
    formState: { isSubmitSuccessful },
    setError,
  } = methods;

  const navigateToLogin = () => {
    navigate("/login");
  };

  useEffect(() => {
    if (isError) {
      setError("pin", { message: "Pin no válido" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  const onSubmitHandler: SubmitHandler<PinInput> = (values) => {
    const allFilled = pin.every((item) => item !== "");
    const joinedPin = pin.join("");
    if (allFilled) {
      sendPin(joinedPin);
    } else {
      showErrorToast("El pin no tiene 4 dígitos");
    }
  };

  const handleChange = (index: number, value: string) => {
    let pinCopy = [...pin];
    pinCopy[index] = value;
    setPin(pinCopy);

    console.log(index, pin.length - 1);

    if (
      value.length === 1 &&
      index < pin.length - 1 &&
      inputRefs.current[index + 1]
    ) {
      inputRefs.current[index + 1]?.focus();
    } else if (
      value.length === 0 &&
      index <= pin.length - 1 &&
      inputRefs.current[index - 1]
    ) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  useEffect(() => {
    if (isError) {
      showErrorToast(formatMessage({ id: "pinpage.error.toast" }));
    }
  }, [isError]);

  if (isSuccess) {
    return (
      <Grid
        container
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "100vh",
          overflowY: "auto",
          width: "100vw",
          padding: 0,
        }}
      >
        <Grid item md={7} sx={{ display: { xs: "none", sm: "block" } }}>
          <Box
            sx={{
              height: "100%",
              backgroundColor: "lightblue", // Solo para visualización
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {/* Contenido de la sección del 60% aquí */}
            <Typography variant="h4">{formatMessage({ id: "pinpage.welcome" })}</Typography>
          </Box>
        </Grid>
        <Grid
          item
          xs={12}
          sm={5}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            minHeight: "100%",
            backgroundImage: `url()`,
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            padding: "1.60rem",
          }}
        >
          <Box
            sx={{
              margin: {
                xs: "2rem 1.625rem", // Margen superior para teléfonos móviles
                sm: "2rem", // Margen superior para tablets
                md: "3rem", // Margen superior para desktop
                // Puedes agregar más breakpoints si es necesario
              },
              height: {
                xs: "5.25rem", // Altura para teléfonos móviles
                sm: "80px", // Altura para tablets
                md: "100px", // Altura para desktop
                // Puedes agregar más breakpoints si es necesario
              },
            }}
          >
            <img src={logo} style={{height: '100%', width: 'auto'}} alt="Logo" />
          </Box>
          <Box
            sx={{
              marginBottom: {
                xs: "2rem", // Margen superior para teléfonos móviles
                sm: "2rem", // Margen superior para tablets
                md: "3rem", // Margen superior para desktop
                // Puedes agregar más breakpoints si es necesario
              },
              height: {
                xs: "5.625rem", // Altura para teléfonos móviles
                sm: "80px", // Altura para tablets
                md: "100px", // Altura para desktop
                // Puedes agregar más breakpoints si es necesario
              },
            }}
          >
            <img src={lock} style={{height: '100%', width: 'auto'}} alt="Logo" />
          </Box>
          <Typography
            textAlign="left"
            component="h1"
            sx={{
              width: "100%",
              fontWeight: 600,
              fontSize: { xs: "1.5rem", md: "2rem", color: "#8F114C" },
              lineHeight: "1.813rem",
              marginBottom: "0.6rem",
            }}
          >
            {formatMessage({ id: "pinpage.instructions" })}
          </Typography>
          <Typography
            sx={{ fontSize: "0.9rem", mb: "3rem", textAlign: "left" }}
          >
            <LinkItem
              to="/login"
              style={{
                color: "#87CDD3",
                fontWeight: "500",
                fontSize: ".875rem",
              }}
            >
              {formatMessage({ id: "pinpage.login" })}
            </LinkItem>
          </Typography>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid
      container
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        height: "100vh",
        width: "100vw",
        padding: 0,
      }}
    >
      <Grid item md={7} sx={{ display: { xs: "none", sm: "block" } }}>
        <Box
          sx={{
            height: "100vh",
            backgroundColor: "lightblue", // Solo para visualización
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {/* Contenido de la sección del 60% aquí */}
          <Typography variant="h4">{formatMessage({ id: "pinpage.welcome" })}</Typography>
        </Box>
      </Grid>
      <Grid
        item
        xs={12}
        sm={5}
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
          minHeight: "100%",
          height: "100%",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          padding: "1.60rem",
          overflowY: "auto"
        }}
      >
        <Box
          sx={{
            margin: {
              xs: "2rem 1.625rem", // Margen superior para teléfonos móviles
              sm: "2rem", // Margen superior para tablets
              md: "3rem", // Margen superior para desktop
              // Puedes agregar más breakpoints si es necesario
            },
            height: {
              xs: "5.25rem", // Altura para teléfonos móviles
              sm: "80px", // Altura para tablets
              md: "100px", // Altura para desktop
              // Puedes agregar más breakpoints si es necesario
            },
          }}
        >
          <img src={logo} style={{height: '100%', width: 'auto'}}  alt="Logo" />
        </Box>
        <Box
          sx={{
            marginBottom: {
              xs: "2rem", // Margen superior para teléfonos móviles
              sm: "2rem", // Margen superior para tablets
              md: "3rem", // Margen superior para desktop
              // Puedes agregar más breakpoints si es necesario
            },
            height: {
              xs: "5.625rem", // Altura para teléfonos móviles
              sm: "80px", // Altura para tablets
              md: "100px", // Altura para desktop
              // Puedes agregar más breakpoints si es necesario
            },
          }}
        >
          <img src={lock} style={{height: '100%', width: 'auto'}}  alt="Logo" />
        </Box>

        <Typography
          textAlign="left"
          component="h1"
          sx={{
            width: "100%",
            fontWeight: 600,
            fontSize: { xs: "1.5rem", md: "2rem", color: "#8F114C" },
            lineHeight: "1.813rem",
            marginBottom: "0.6rem",
          }}
        >
          {formatMessage({ id: "pinpage.code.title" })}
        </Typography>
        <Typography
          textAlign="left"
          component="span"
          sx={{
            width: "100%",
            fontWeight: 400,
            fontSize: { xs: "1rem" },
            marginBottom: "2rem",
          }}
        >
          {formatMessage({ id: "pinpage.code.instructions" })}
        </Typography>

        <FormProvider {...methods}>
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmitHandler)}
            noValidate
            autoComplete="on" // Set autoComplete to "on" to enable browser autocomplete
            maxWidth="27rem"
          >
            <Box
              sx={{
                display: "flex",
                gap: "15px",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {pin.map((value: any, index: number) => (
                <input
                  key={index}
                  type="text"
                  maxLength={1}
                  value={value}
                  onChange={(e) => handleChange(index, e.target.value)}
                  ref={(el) => (inputRefs.current[index] = el)}
                  //onKeyDown={(e) => handleBackspace(index, e)}
                  //ref={(input) => (inputs.current[index] = input)}
                  //onKeyDown={(e) => handleKeyDown(index, e)}
                  style={{
                    width: "60px",
                    height: "60px",
                    outline: "none",
                    borderRadius: "18px",
                    backgroundColor: "#F3F3F3",
                    fontSize: "16px",
                    fontWeight: "500",
                    textAlign: "center",
                    border: "none",
                  }}
                />
              ))}
            </Box>
            <Typography
              sx={{
                fontSize: "0.9rem",
                mb: "3rem",
                mt: "1.5rem",
                textAlign: "left",
              }}
            >
              <LinkItem
                to="/login"
                style={{
                  color: "#87CDD3",
                  fontWeight: "500",
                  fontSize: ".875rem",
                }}
              >
                          {formatMessage({ id: "pinpage.send.again" })}
              </LinkItem>
            </Typography>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "1rem",
              }}
            >
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  backgroundColor: "#8F114C",
                  width: "100%",
                  height: "4.188rem",
                  fontSize: "1.125rem",
                  fontWeight: 600,
                  borderRadius: "1.188rem",
                  textTransform: "none",
                }}
                disableElevation
                type="submit"
              >
                {formatMessage({ id: "pinpage.confirm" })}                
              </Button>
            </Box>
            <Box
              sx={{
                marginBottom: "2rem",
              }}
            >
              <LinkItem to="/forgot-password">
                <Typography
                  textAlign="center"
                  sx={{
                    marginTop: ".3rem",
                    color: "#87CDD3",
                    fontWeight: "600",
                    fontSize: ".875rem",
                    lineHeight: ".946rem",
                  }}
                >
                  {formatMessage({ id: "pinpage.cancel" })}                   
                </Typography>
              </LinkItem>
            </Box>
          </Box>
        </FormProvider>
      </Grid>
    </Grid>
  );
};